import { io } from 'socket.io-client';
import notificationSoundService from '../services/notificationSoundService';

function getSocketUrl() {
  const isDevHost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  const envVal =
    typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_API_URL;

  if (isDevHost) {
    return envVal || "http://localhost:45459";
  }

  if (envVal) {
    return envVal;
  }
  return window.location.origin;
}

class WebSocketService {
  constructor() {
    this.socket = null;
    this.onNotification = () => {};
    this.onAllNotifications = () => {};
    this.onNotificationRead = () => {};
    this.onAllRead = () => {};
  }

  connect(userId, mda_name) {
    try {
      const url = getSocketUrl();
      console.log("Connecting Socket.io for user:", userId, "to", url);

      this.socket = io(url, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 16000,
      });

      this.socket.on('connect', () => {
        console.log("Socket.io connected:", this.socket.id);
        this.socket.emit('connect_user', { userId, mda_name });
        this.socket.emit('fetchNotifications', { userId, limit: 50 });
      });

      this.socket.on('notification', (data) => {
        console.log("Notification received:", data);
        const enhanced = this.enhanceNotification(data.notification || data);
        setTimeout(() => notificationSoundService.playSound(enhanced.priority?.toLowerCase() || 'info'), 100);
        this.onNotification(enhanced);
      });

      this.socket.on('allNotifications', (data) => {
        console.log("All notifications received:", data.notifications);
        this.onAllNotifications(data.notifications || []);
      });

      this.socket.on('notificationRead', (data) => {
        this.onNotificationRead(data.id);
      });

      this.socket.on('allRead', () => {
        this.onAllRead();
      });

      this.socket.on('disconnect', (reason) => {
        console.log("Socket.io disconnected:", reason);
      });

      this.socket.on('connect_error', (error) => {
        console.error("Socket.io connection error:", error.message);
      });

      this.socket.on('error', (data) => {
        console.error("Socket.io error:", data);
      });

    } catch (error) {
      console.error('Socket.io setup failed:', error);
    }
  }

  enhanceNotification(notification) {
    const priority = notification.priority || this.determinePriority(notification);
    const category = notification.category || this.determineCategory(notification);

    return {
      ...notification,
      priority,
      category,
      id: notification.id || Date.now() + Math.random(),
    };
  }

  determinePriority(notification) {
    const m = (notification.message || '').toLowerCase();
    const t = (notification.notification_type || '').toLowerCase();
    if (m.includes('urgent') || t.includes('urgent') || m.includes('immediate')) return 'URGENT';
    if (m.includes('approval') || t.includes('approval') || m.includes('release') || t.includes('release')) return 'IMPORTANT';
    if (m.includes('approved') || m.includes('completed') || m.includes('success')) return 'SUCCESS';
    return 'INFO';
  }

  determineCategory(notification) {
    const m = (notification.message || '').toLowerCase();
    const t = (notification.notification_type || '').toLowerCase();
    if (m.includes('approval') || t.includes('approval')) return 'approval';
    if (m.includes('release') || t.includes('release')) return 'release';
    if (m.includes('payment') || t.includes('payment')) return 'payment';
    if (m.includes('system') || t.includes('system')) return 'system';
    return 'general';
  }

  subscribeToNotifications(callback) {
    this.onNotification = callback;
  }

  subscribeToAllNotifications(callback) {
    this.onAllNotifications = callback;
  }

  notificationRead(callback) {
    this.onNotificationRead = callback;
  }

  onAllRead(callback) {
    this.onAllRead = callback;
  }

  sendNotification(notification) {
    if (this.socket?.connected) {
      this.socket.emit('sendNotification', { notification });
    }
  }

  markAsRead(notificationId) {
    if (this.socket?.connected) {
      this.socket.emit('markAsRead', { notificationId });
    }
  }

  markAllAsRead(userId) {
    if (this.socket?.connected) {
      this.socket.emit('markAllRead', { userId });
    }
  }

  fetchNotifications(options = {}) {
    if (this.socket?.connected) {
      this.socket.emit('fetchNotifications', options);
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  isConnected() {
    return this.socket?.connected || false;
  }
}

export default new WebSocketService();
